// Printable reseller delivery report — who received what, when, how many
// pieces, and for how much. Same letterhead header convention as
// InvoiceDocument, and the same proven-safe minHeight value (220mm, not
// StockReportDocument's 278mm) — InvoiceDocument's own comment documents
// that 278mm/297mm/250mm all overflowed onto a near-empty phantom 2nd page
// on at least one real print setup; that's exactly what a short delivery
// report (few rows) hit here. A long report still simply grows past 220mm
// and flows across pages normally — minHeight only ever adds space, it
// never truncates or forces a page break on its own.
const NAVY = '#1B2A4A';
const GOLD = '#AE8A4C';
const INK = '#26324A';
const MUTED = '#6B7280';
const HAIRLINE = '#E7E9EE';
const SOFT_BG = '#F6F7FB';
const GOLD_WASH = '#FBF6EC';

export interface DeliveryReportBranding {
  issuer_name: string | null;
  issuer_address: string | null;
  issuer_contact: string | null;
  issuer_email: string | null;
  logo_url: string | null;
}

export interface DeliveryReportRow {
  order_no: string;
  partner_name: string;
  // core_orders has no dedicated delivered_at timestamp (only status +
  // placed_at) — this is the order date, not a separate delivery date.
  // The report is scoped to status='delivered' orders by the page that
  // loads it, so "delivered" is communicated via filterLabel instead.
  order_date: string;
  pcs: number;
  amount_bdt: number;
}

function formatDateTime(d: Date): string {
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function DeliveryReportDocument({
  branding,
  rows,
  generatedAt,
  filterLabel,
}: {
  branding: DeliveryReportBranding;
  rows: DeliveryReportRow[];
  generatedAt: Date;
  filterLabel: string;
}) {
  const brandName = branding.issuer_name?.trim() || 'The.K Seoul';
  const totalPcs = rows.reduce((sum, r) => sum + r.pcs, 0);
  const totalAmount = rows.reduce((sum, r) => sum + r.amount_bdt, 0);

  return (
    <div
      className="stock-report-doc bg-white mx-auto"
      style={{
        width: '210mm',
        minHeight: '220mm',
        fontFamily: "'Inter', system-ui, sans-serif",
        color: INK,
        boxSizing: 'border-box',
      }}
    >
      <div style={{ padding: '0 16mm', boxSizing: 'border-box' }}>
        <table className="w-full" style={{ borderCollapse: 'collapse' }}>
          {/* The letterhead lives in <thead> (not a plain sibling <div>
              above the table) specifically so it repeats at the top of
              every printed page — a plain div has no native way to repeat
              across pages at all; <thead> is the one element browsers
              reliably do repeat. */}
          <thead style={{ display: 'table-header-group' }}>
            <tr>
              <td colSpan={6} style={{ padding: '8mm 0 0' }}>
                <div className="flex items-center justify-between gap-8">
                  <div className="flex items-center gap-3.5 min-w-0">
                    {branding.logo_url ? (
                      <img src={branding.logo_url} alt={brandName} crossOrigin="anonymous" className="flex-shrink-0 object-contain" style={{ height: '64px', maxWidth: '220px' }} />
                    ) : (
                      <h1 className="m-0" style={{ fontFamily: "'Fraunces', Georgia, serif", fontWeight: 600, fontSize: '26px', letterSpacing: '-0.015em', color: NAVY }}>
                        {brandName}
                      </h1>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0" style={{ fontSize: '11px', color: MUTED, lineHeight: 1.5 }}>
                    {branding.issuer_address && <p className="m-0">{branding.issuer_address}</p>}
                    {branding.issuer_contact && <p className="m-0">Hotline: {branding.issuer_contact}</p>}
                    {branding.issuer_email && <p className="m-0">{branding.issuer_email}</p>}
                  </div>
                </div>

                <div style={{ borderTop: `1px solid ${GOLD}`, marginTop: '10px' }} />
                <div style={{ borderTop: `2px solid ${NAVY}`, marginTop: '3px' }} />
              </td>
            </tr>
          </thead>
          <tbody>
            {/* Title, then the data table, in one body row. The title must
                sit ABOVE the column-header row on page 1 yet appear only
                once, while the column-header row must still repeat on
                every page — so the column headers are the <thead> of a
                nested table instead of a second row of the outer <thead>
                (which put them above the title on page 1). Chrome repeats
                a nested table's <thead> too. This wrapper row opts out of
                the global .stock-report-doc tr break-inside:avoid so it
                can flow across pages; the data rows inside still avoid
                splitting. */}
            <tr className="report-flow-row">
              <td colSpan={6} style={{ padding: 0 }}>
                <div className="flex items-end justify-between" style={{ paddingTop: '14px' }}>
                  <div>
                    <p className="m-0" style={{ fontFamily: "'Fraunces', Georgia, serif", fontWeight: 600, fontSize: '20px', color: NAVY }}>
                      Reseller Delivery Report
                    </p>
                    <p className="m-0" style={{ fontSize: '10.5px', color: MUTED }}>
                      {filterLabel} · Generated {formatDateTime(generatedAt)}
                    </p>
                  </div>
                  <p className="m-0" style={{ fontSize: '10.5px', color: MUTED }}>
                    {rows.length} order(s)
                  </p>
                </div>
                <table className="w-full" style={{ borderCollapse: 'collapse', marginTop: '14px' }}>
                  <thead style={{ display: 'table-header-group' }}>
                    <tr style={{ borderBottom: `2px solid ${NAVY}` }}>
                      <th className="text-right" style={{ padding: '8px 10px', fontSize: '9px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: NAVY }}>
                        Sr.
                      </th>
                      <th className="text-left" style={{ padding: '8px 10px', fontSize: '9px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: NAVY }}>
                        Order No
                      </th>
                      <th className="text-left" style={{ padding: '8px 10px', fontSize: '9px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: NAVY }}>
                        Partner
                      </th>
                      <th className="text-left" style={{ padding: '8px 10px', fontSize: '9px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: NAVY }}>
                        Date
                      </th>
                      <th className="text-right" style={{ padding: '8px 10px', fontSize: '9px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: NAVY }}>
                        Pcs
                      </th>
                      <th className="text-right" style={{ padding: '8px 10px', fontSize: '9px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: NAVY }}>
                        Amount (৳)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={`${r.order_no}-${i}`} style={{ background: i % 2 === 1 ? SOFT_BG : 'transparent', borderBottom: `1px solid ${HAIRLINE}` }}>
                        <td style={{ padding: '8px 10px', fontSize: '11px', color: MUTED, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{i + 1}</td>
                        <td style={{ padding: '8px 10px', fontSize: '11px', color: MUTED, letterSpacing: '0.02em' }}>{r.order_no}</td>
                        <td style={{ padding: '8px 10px', fontSize: '11.5px', fontWeight: 500 }}>{r.partner_name}</td>
                        <td style={{ padding: '8px 10px', fontSize: '11px', color: MUTED }}>{new Date(r.order_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                        <td style={{ padding: '8px 10px', fontSize: '11.5px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{r.pcs.toLocaleString()}</td>
                        <td style={{ padding: '8px 10px', fontSize: '11.5px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: NAVY }}>{r.amount_bdt.toLocaleString()}</td>
                      </tr>
                    ))}
                    {/* The grand total as the table's last BODY row, not a <tfoot>
                        — a <tfoot> repeats at the bottom of every printed page a
                        table spans (standard browser table-printing behavior,
                        same as <thead> repeating at the top of each), which is
                        right for the header but showed this same running total on
                        every page instead of only once at the true end. As a
                        normal last row it only ever appears once, wherever the
                        real last row of data falls (the last page). */}
                    <tr style={{ background: GOLD_WASH }}>
                      <td colSpan={4} style={{ padding: '9px 10px', fontSize: '11.5px', fontWeight: 700, color: NAVY, borderTop: `2.5px solid ${NAVY}`, letterSpacing: '0.03em', textTransform: 'uppercase' }}>
                        Total
                      </td>
                      <td style={{ padding: '9px 10px', fontSize: '13px', fontWeight: 700, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: NAVY, borderTop: `2.5px solid ${NAVY}` }}>
                        {totalPcs.toLocaleString()}
                      </td>
                      <td style={{ padding: '9px 10px', fontSize: '13px', fontWeight: 700, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: NAVY, borderTop: `2.5px solid ${NAVY}` }}>
                        ৳{totalAmount.toLocaleString()}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
